import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, createBlock as _createBlock, withModifiers as _withModifiers, resolveDynamicComponent as _resolveDynamicComponent, KeepAlive as _KeepAlive } from "vue"

const _hoisted_1 = { class: "platform-layout" }
const _hoisted_2 = { class: "platform-header" }
const _hoisted_3 = { class: "header-brand" }
const _hoisted_4 = { class: "header-title" }
const _hoisted_5 = { class: "header-right" }
const _hoisted_6 = { class: "user-name" }
const _hoisted_7 = { class: "platform-body" }
const _hoisted_8 = { class: "sidebar-scroll" }
const _hoisted_9 = ["title", "onClick"]
const _hoisted_10 = { class: "menu-text" }
const _hoisted_11 = { class: "menu-children" }
const _hoisted_12 = ["title", "onClick"]
const _hoisted_13 = {
  key: 0,
  class: "menu-dot"
}
const _hoisted_14 = { class: "menu-text" }
const _hoisted_15 = { class: "platform-main" }
const _hoisted_16 = { class: "platform-tabs" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "platform-content" }

import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import {
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons-vue'
import { APP_MENU_GROUPS, ROUTE_MENU_MAP, type AppMenuItem } from '@/config/menu'
import { useLocaleStore } from '@/stores/locale'
import { useTabsStore } from '@/stores/tabs'
import type { AppLocale } from '@/i18n'

const STORAGE_KEY_COLLAPSE = 'rcs.sidebar.collapsed'
const STORAGE_KEY_GROUPS = 'rcs.sidebar.openGroups'

type BizLine = 'realtime' | 'compliance'

export default /*@__PURE__*/_defineComponent({
  __name: 'BasicLayout',
  setup(__props) {

const { t } = useI18n()
const localeStore = useLocaleStore()
const tabsStore = useTabsStore()
const route = useRoute()
const router = useRouter()

const collapsed = ref(false)

// 记忆折叠状态
try {
  collapsed.value = localStorage.getItem(STORAGE_KEY_COLLAPSE) === 'true'
} catch { /* ignore */ }

const menuGroups = APP_MENU_GROUPS

const activeBizLine = ref<BizLine>('realtime')

const currentBizLabel = computed(() =>
  activeBizLine.value === 'compliance' ? t('layout.bizCompliance') : t('layout.bizRealtimeTxn')
)

function onBizSelect({ key }: { key: string }) {
  activeBizLine.value = key as BizLine
}

function initOpenGroups(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_GROUPS)
    if (saved) return JSON.parse(saved)
  } catch { /* ignore */ }
  return Object.fromEntries(APP_MENU_GROUPS.map(group => [group.id, !!group.defaultOpen]))
}

const openGroups = reactive<Record<string, boolean>>(initOpenGroups())

function persistOpenGroups(): void {
  try { localStorage.setItem(STORAGE_KEY_GROUPS, JSON.stringify(openGroups)) } catch { /* ignore */ }
}

const activeMenuKey = computed(() => {
  const menuKey = route.meta.menuKey as string | undefined
  return menuKey ?? tabsStore.activeKey
})

const currentLanguageLabel = computed(() =>
  localeStore.locale === 'zh-CN' ? t('layout.zhCN') : t('layout.enUS')
)

function tabTitle(tab: { key: string; title: string }): string {
  const menu = ROUTE_MENU_MAP[tab.key]
  return menu ? t(menu.labelKey) : tab.title
}

function toggleGroup(id: string): void {
  if (collapsed.value) {
    collapsed.value = false
    try { localStorage.setItem(STORAGE_KEY_COLLAPSE, 'false') } catch { /* ignore */ }
  }
  // 互斥展开：打开当前组，关闭其他组
  const willOpen = !openGroups[id]
  if (willOpen) {
    for (const gid of Object.keys(openGroups)) {
      openGroups[gid] = gid === id
    }
  } else {
    openGroups[id] = false
  }
  persistOpenGroups()
}

function toggleCollapse(): void {
  collapsed.value = !collapsed.value
  try { localStorage.setItem(STORAGE_KEY_COLLAPSE, String(collapsed.value)) } catch { /* ignore */ }
}

function openPage(item: AppMenuItem): void {
  tabsStore.openTab(item.key, t(item.labelKey), item.path)
  router.push(item.path)
}

function switchTab(key: string): void {
  const tab = tabsStore.openTabs.find(item => item.key === key)
  if (!tab) return
  tabsStore.switchTab(key)
  router.push(tab.path)
}

function closeTab(key: string): void {
  const closed = tabsStore.closeTab(key)
  if (!closed) {
    message.warning(t('layout.keepOneTab'))
    return
  }
  const active = tabsStore.openTabs.find(item => item.key === tabsStore.activeKey)
  if (active) router.push(active.path)
}

function onLanguageSelect({ key }: { key: string }): void {
  localeStore.setLocale(key as AppLocale)
}

function syncRouteTab(routeName?: string | null, menuKey?: string | null, title?: string, path?: string): void {
  const key = menuKey ?? routeName
  if (!key) return
  const menu = ROUTE_MENU_MAP[key]
  if (!menu) return
  tabsStore.openTab(key, title ?? t(menu.labelKey), path ?? menu.path)
}

watch(
  () => route.fullPath,
  () => {
    const menuKey = (route.meta.menuKey as string) ?? (route.name as string)
    const titleKey = route.meta.titleKey as string | undefined
    syncRouteTab(route.name as string, menuKey, titleKey ? t(titleKey) : undefined, route.fullPath)
  },
  { immediate: true }
)

watch(
  () => localeStore.locale,
  () => {
    tabsStore.openTabs.forEach(tab => {
      const menu = ROUTE_MENU_MAP[tab.key]
      if (menu) tab.title = t(menu.labelKey)
    })
  }
)

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_menu_item = _resolveComponent("a-menu-item")!
  const _component_a_menu = _resolveComponent("a-menu")!
  const _component_a_dropdown = _resolveComponent("a-dropdown")!
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[0] || (_cache[0] = _createElementVNode("div", { class: "header-logo" }, null, -1)),
        _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('layout.productName')), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_a_dropdown, null, {
          overlay: _withCtx(() => [
            _createVNode(_component_a_menu, { onClick: onBizSelect }, {
              default: _withCtx(() => [
                _createVNode(_component_a_menu_item, { key: "realtime" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('layout.bizRealtimeTxn')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_menu_item, { key: "compliance" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('layout.bizCompliance')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_component_a_button, { class: "header-chip" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(currentBizLabel.value), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_a_dropdown, null, {
          overlay: _withCtx(() => [
            _createVNode(_component_a_menu, { onClick: onLanguageSelect }, {
              default: _withCtx(() => [
                _createVNode(_component_a_menu_item, { key: "zh-CN" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('layout.zhCN')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_menu_item, { key: "en-US" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('layout.enUS')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_component_a_button, {
              type: "text",
              class: "lang-btn"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(GlobalOutlined)),
                _createTextVNode(" " + _toDisplayString(currentLanguageLabel.value), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "header-divider" }, null, -1)),
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "user-avatar" }, null, -1)),
        _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)('layout.userName')), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("aside", {
        class: _normalizeClass(["platform-sidebar", { collapsed: collapsed.value }])
      }, [
        _createElementVNode("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(menuGroups), (group) => {
            return (_openBlock(), _createElementBlock("div", {
              key: group.id,
              class: "menu-group"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["menu-group-title", { 'has-children': group.children.length > 0, expanded: openGroups[group.id] }]),
                title: collapsed.value ? _unref(t)(group.labelKey) : '',
                onClick: ($event: any) => (toggleGroup(group.id))
              }, [
                _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)(group.labelKey)), 1)
              ], 10, _hoisted_9),
              _createVNode(_Transition, { name: "menu-slide" }, {
                default: _withCtx(() => [
                  _withDirectives(_createElementVNode("div", _hoisted_11, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.children, (item) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: item.key,
                        class: _normalizeClass(["menu-item", { active: activeMenuKey.value === item.key }]),
                        title: collapsed.value ? _unref(t)(item.labelKey) : '',
                        onClick: ($event: any) => (openPage(item))
                      }, [
                        (activeMenuKey.value === item.key)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_13))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(t)(item.labelKey)), 1)
                      ], 10, _hoisted_12))
                    }), 128))
                  ], 512), [
                    [_vShow, openGroups[group.id] && group.children.length > 0]
                  ])
                ]),
                _: 2
              }, 1024)
            ]))
          }), 128))
        ]),
        _createVNode(_component_a_tooltip, {
          title: collapsed.value ? _unref(t)('layout.expand') : _unref(t)('layout.collapse'),
          placement: "right"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", {
              class: "sidebar-footer",
              onClick: toggleCollapse
            }, [
              (!collapsed.value)
                ? (_openBlock(), _createBlock(_unref(MenuFoldOutlined), { key: 0 }))
                : (_openBlock(), _createBlock(_unref(MenuUnfoldOutlined), { key: 1 }))
            ])
          ]),
          _: 1
        }, 8, ["title"])
      ], 2),
      _createElementVNode("main", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tabsStore).openTabs, (tab) => {
            return (_openBlock(), _createElementBlock("div", {
              key: tab.key,
              class: _normalizeClass(["platform-tab", { active: _unref(tabsStore).activeKey === tab.key }]),
              onClick: ($event: any) => (switchTab(tab.key))
            }, [
              _createElementVNode("span", null, _toDisplayString(tabTitle(tab)), 1),
              (_unref(tabsStore).openTabs.length > 1)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: "tab-close",
                    onClick: _withModifiers(($event: any) => (closeTab(tab.key)), ["stop"])
                  }, "×", 8, _hoisted_18))
                : _createCommentVNode("", true)
            ], 10, _hoisted_17))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_19, [
          _createVNode(_component_router_view, null, {
            default: _withCtx(({ Component }) => [
              (_openBlock(), _createBlock(_KeepAlive, null, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(Component)))
              ], 1024))
            ]),
            _: 1
          })
        ])
      ])
    ])
  ]))
}
}

})